import * as Yup from 'yup';
import { Formik } from 'formik';
import React, { Fragment } from 'react';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { DateInput } from 'components/layout/form-fields/inputs/DateInput';
import { shift } from '@floating-ui/dom';
import { jobApplicationStore } from './JobApplicationStore';
import { eventManager } from 'ats/utils/EventManager';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { UsersDropdown } from 'components/layout/form-fields/inputs/UsersDropdown';
import { ApplicantDropdown } from 'components/layout/form-fields/inputs/ApplicantDropdown';
import { JobPositionDropdown } from 'components/layout/form-fields/inputs/JobPositionDropdown';
import { FormField } from 'components/layout/modals/components';
import { FormColumn } from 'components/layout/modals/components/FormColumn';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { getJobApplicationTaskDropdownOptions } from 'ats/utils/jobApplicationTaskTheme';
import { authStore } from 'shared/security/AuthStore';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import { dialogStore } from 'components/layout/modals/DialogStore';
import {
	JobApplicationTaskOccurredOnField,
	occurredOnSchema,
	toOccurredOnValue
} from './JobApplicationTaskOccurredOnField';

const CLOSED_JOB_WARNING =
	'This job position is Closed. A pending left here may not be visible to whoever should act on it.';

const CLOSED_JOB_DIALOG = {
	title: 'Closed Job Position',
	message: `${CLOSED_JOB_WARNING} Do you want to continue?`,
	confirmLabel: 'Continue',
	confirmButtonClass: 'is-primary'
};

function isClosedJob(job) {
	return job?.status === 'closed';
}

const ValidationSchema = Yup.object().shape({
	applicant: Yup.object().nullable().label('Candidate'),
	job: Yup.object().nullable().label('Job Position'),
	description: Yup.string().label('Description'),
	type: Yup.object().label('Type').required(),
	scheduledDate: Yup.string().label('Scheduled Date').nullable(),
	occurredOn: occurredOnSchema,
	assignments: Yup.array().nullable().label('Assignments')
});

export class JobApplicationTaskCreate extends ModalComponent {
	static isV2 = true;

	getInitialValues() {
		return {
			applicant: this.props.applicant || null,
			job: this.props.job || null,
			description: '',
			type: null,
			scheduledDate: null,
			occurredOn: null,
			assignments: authStore.profile ? [authStore.profile] : []
		};
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				onSubmit={values => {
					this.handleSubmit(values);
				}}>
				{formikBag =>
					this.renderModalContainer(formikBag, 'JobApplicationTaskCreate')
				}
			</Formik>
		);
	}

	getTargetJob(values) {
		return this.props.job || values?.job;
	}

	handleSubmit(values) {
		if (isClosedJob(this.getTargetJob(values))) {
			dialogStore.openDialog(CLOSED_JOB_DIALOG, () => {
				this.saving();
				this.create(values);
			});
			return;
		}

		this.saving();
		this.create(values);
	}

	renderForm(formikBag) {
		const selectedJob = this.getTargetJob(formikBag.values);
		const showClosedJobWarning = isClosedJob(selectedJob);

		return (
			<Fragment>
				<FormField label="Task" required error={formikBag.errors.type}>
					<Dropdown
						value={formikBag.values.type}
						onChange={item => {
							formikBag.setFieldValue('type', item);
						}}
						options={getJobApplicationTaskDropdownOptions(
							jobApplicationStore.taskOptions
						)}
						menuClassName="job-task-type-menu"
						placeholder=" "
					/>
				</FormField>
				{jobApplicationStore.schedulableTaskTypes.includes(
					formikBag.values.type?.value
				) ? (
					<FormField
						label="Scheduled Date"
						error={formikBag.errors.scheduledDate}>
						<DateInput
							value={formikBag.values.scheduledDate}
							popperPlacement="top-start"
							popperModifiers={[
								shift({
									padding: 5,
									crossAxis: true
								})
							]}
							onBlur={() => formikBag.setFieldTouched('scheduledDate')}
							onChange={newDate => {
								formikBag.setFieldValue('scheduledDate', newDate);
							}}
							isDateTime
						/>
					</FormField>
				) : null}
				<JobApplicationTaskOccurredOnField formikBag={formikBag} />
				<div className="job-field-with-warning">
					<FormField label="Job">
						<JobPositionDropdown
							onValueChange={item => {
								formikBag.setFieldValue('job', item);
							}}
							value={this.props.job || formikBag.values.job}
							placeholder=" "
							disabled={!!this.props.job}
							filterByApplicantId={this.props.applicant?.id}
						/>
					</FormField>
					{showClosedJobWarning && (
						<div className="form-field-warning" role="status">
							<img src="/ats/icons/warning-icon.svg" alt="" />
							<span>{CLOSED_JOB_WARNING}</span>
						</div>
					)}
				</div>
				<FormField label="Candidate">
					<ApplicantDropdown
						placeholder=" "
						onValueChange={value => {
							formikBag.setFieldValue('applicant', value);
						}}
						value={this.props.applicant || formikBag.values.applicant}
						showAvatar
						disabled={!!this.props.applicant}
						filterByJobId={this.props.job?.id}
					/>
				</FormField>
				<FormField label="Assigned to" error={formikBag.errors.assignments}>
					<UsersDropdown
						value={formikBag.values.assignments}
						isMulti
						placeholder=" "
						role={NON_CANDIDATE_USER_ROLES}
						onValueChange={values => {
							formikBag.setFieldValue('assignments', values);
						}}
						showAvatar
					/>
				</FormField>
				<FormColumn>
					<label className="form-field-label-outside">Description</label>
					<FormField error={formikBag.errors.description}>
						<FormikFormField
							className="has-text-left"
							field="description"
							type="textarea"
							placeholder="Description"
							maxlength={2500}
						/>
					</FormField>
				</FormColumn>
			</Fragment>
		);
	}

	async resolveJobApplicationId(values) {
		if (!this.props.job) {
			return values.job?.applicants?.find(
				({ applicant }) => applicant.id === this.props.applicant.id
			)?.id;
		}

		const findForJob = jobApplications =>
			jobApplications?.find(
				jobApplication => jobApplication.jobId === this.props.job.id
			)?.id;

		const fromApplicant = findForJob(values.applicant?.jobApplications);
		if (fromApplicant || !values.applicant?.id) return fromApplicant;

		// The candidate search omits jobApplications, so look them up on demand.
		const result = await jobApplicationStore
			.getAllByApplicantId(values.applicant.id)
			.catch(() => null);
		return findForJob(result?.data?.rows);
	}

	async create(values) {
		const jobApplicationId = await this.resolveJobApplicationId(values);
		if (!jobApplicationId) {
			this.resultNotification(
				{
					status: 'fail',
					message: 'Select a candidate who has applied to this job.'
				},
				'Task',
				'Create'
			);
			return;
		}

		jobApplicationStore
			.createTask(jobApplicationId, {
				description: values.description,
				type: values.type.value,
				scheduledDate: jobApplicationStore.schedulableTaskTypes.includes(
					values.type?.value
				)
					? values.scheduledDate
					: null,
				occurredOn: jobApplicationStore.isMilestoneTaskType(values.type?.value)
					? toOccurredOnValue(values.occurredOn)
					: null,
				assignments: values.assignments.map(assignment => assignment.id)
			})
			.then(result => {
				if (result.status === 'success') {
					eventManager.emit('taskChanged');
				}
				this.resultNotification(result, 'Task', 'Create');
			});
	}
}
